import {
  ICompanyReport,
  ICompanyReportPoint,
  CompanyReportMetricKey,
} from '@/types/response';
import moment from 'moment';

export type Unit = 'week' | 'month';

/** まとめたグラフに出す指標。並び順がそのまま色の順になる */
export type MainMetricId =
  | 'drivingLowScore'
  | 'cogevoPerUser'
  | 'stepsPerDay'
  | 'familyActivity';

export interface IMainMetric {
  id: MainMetricId;
  label: string;
  unit: string;
  /** 小数の桁数 */
  digits: number;
  /** 定義（グラフの下に出す） */
  note: string;
}

export const MAIN_METRICS: IMainMetric[] = [
  {
    id: 'drivingLowScore',
    label: '運転スコア80点以下の人数',
    unit: '人',
    digits: 1,
    note: '週に1回でも80点以下のトリップがあった人',
  },
  {
    id: 'cogevoPerUser',
    label: '脳トレ 1人あたり実施回数',
    unit: '回',
    digits: 1,
    note: '週の実施回数 ÷ その週に実施した人数',
  },
  {
    id: 'stepsPerDay',
    label: '歩数（1日平均）',
    unit: '歩',
    digits: 0,
    note: '歩数のあった日の平均',
  },
  {
    id: 'familyActivity',
    label: '家族の活動量',
    unit: '件',
    digits: 0,
    note: '絵日記・コメント・いいね・家族トークの合計（週あたり）',
  },
];

export interface IReportBucket {
  /** 週は weekStart、月は YYYY-MM */
  key: string;
  /** 軸の表示（M/D または YYYY/M） */
  label: string;
  /** 期間（YYYY-MM-DD） */
  from: string;
  to: string;
  /** 今日を含む（まだ途中の）期間 */
  partial: boolean;
  /** 値が無い（分母が 0 の）ときは null */
  values: Record<MainMetricId, number | null>;
}

type Week = ICompanyReport['series'][number];

const point = (w: Week, key: CompanyReportMetricKey): ICompanyReportPoint =>
  w.metrics[key] ?? { value: 0, base: null };

const ratio = (value: number, base: number): number | null =>
  base > 0 ? value / base : null;

const activityOf = (w: Week): number =>
  point(w, 'familyPosts').value +
  point(w, 'familyComments').value +
  point(w, 'familyLikes').value +
  point(w, 'familyTalk').value;

const addDays = (date: string, days: number) =>
  moment(date, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD');

/**
 * 週次の合計を、週または月の区切りに直す。
 *
 * - 週: 運転は運転した人がいない週、脳トレ・歩数は分母が 0 の週を null（データなし）にする
 * - 月: 週の始まり（月曜）が属する月にまとめる。人数・件数は「月内の週の平均」、
 *   1人あたり・1日平均は月内の合計どうしで割る（週ごとの比率を平均しない）
 */
export const toReportBuckets = (
  weeks: Week[],
  unit: Unit,
  today: string,
): IReportBucket[] => {
  if (unit === 'week') {
    return weeks.map((w) => {
      const to = addDays(w.weekStart, 6);
      const driving = point(w, 'drivingLowScoreUsers');
      const cogevo = point(w, 'cogevoPlays');
      const steps = point(w, 'steps');

      return {
        key: w.weekStart,
        label: moment(w.weekStart, 'YYYY-MM-DD').format('M/D'),
        from: w.weekStart,
        to,
        partial: to >= today,
        values: {
          drivingLowScore: (driving.base ?? 0) > 0 ? driving.value : null,
          cogevoPerUser: ratio(cogevo.value, cogevo.base ?? 0),
          stepsPerDay: ratio(steps.value, steps.base ?? 0),
          familyActivity: activityOf(w),
        },
      };
    });
  }

  const months = new Map<string, Week[]>();
  for (const w of weeks) {
    const key = w.weekStart.slice(0, 7);
    months.set(key, [...(months.get(key) ?? []), w]);
  }

  return Array.from(months.entries()).map(([key, group]) => {
    const sum = (k: CompanyReportMetricKey, field: 'value' | 'base') =>
      group.reduce((acc, w) => acc + (point(w, k)[field] ?? 0), 0);
    const drivingWeeks = group.filter(
      (w) => (point(w, 'drivingLowScoreUsers').base ?? 0) > 0,
    );
    const to = addDays(group[group.length - 1].weekStart, 6);

    return {
      key,
      label: moment(key, 'YYYY-MM').format('YYYY/M'),
      from: group[0].weekStart,
      to,
      partial: to >= today,
      values: {
        drivingLowScore: drivingWeeks.length
          ? drivingWeeks.reduce(
              (acc, w) => acc + point(w, 'drivingLowScoreUsers').value,
              0,
            ) / drivingWeeks.length
          : null,
        cogevoPerUser: ratio(
          sum('cogevoPlays', 'value'),
          sum('cogevoPlays', 'base'),
        ),
        stepsPerDay: ratio(sum('steps', 'value'), sum('steps', 'base')),
        familyActivity:
          group.reduce((acc, w) => acc + activityOf(w), 0) / group.length,
      },
    };
  });
};

export interface IIndexed {
  /** 基準にした期間（最初に値が 0 より大きかった期間）。基準が無ければ null */
  baseKey: string | null;
  baseValue: number | null;
  /** 基準 = 100 の指数（小数1桁）。値が無い期間は null */
  points: (number | null)[];
  /** 値はあるが期間中ずっと 0（基準が作れない）。データなしとは区別する */
  allZero: boolean;
}

/**
 * 単位の違う指標を1つのグラフに重ねるため、最初に値が 0 より大きかった期間を 100 にした指数にする。
 * 0 を基準にすると割れないので、0 の期間は飛ばして次の期間を基準にする。
 */
export const toIndex = (
  buckets: IReportBucket[],
  id: MainMetricId,
): IIndexed => {
  const base = buckets.find((b) => {
    const v = b.values[id];

    return typeof v === 'number' && v > 0;
  });
  if (!base)
    return {
      baseKey: null,
      baseValue: null,
      points: buckets.map(() => null),
      allZero: buckets.some((b) => b.values[id] === 0),
    };
  const baseValue = base.values[id] as number;

  return {
    baseKey: base.key,
    baseValue,
    allZero: false,
    points: buckets.map((b) => {
      const v = b.values[id];

      return typeof v === 'number'
        ? Math.round((v / baseValue) * 1000) / 10
        : null;
    }),
  };
};

export const formatValue = (
  v: number | null | undefined,
  digits: number,
  unit = '',
): string =>
  typeof v === 'number'
    ? `${v.toLocaleString('ja-JP', { maximumFractionDigits: digits, minimumFractionDigits: 0 })}${unit}`
    : '—';

export const percent = (
  numerator: number,
  denominator: number,
): number | null =>
  denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : null;

/** みんなの生活診断: 要注意件数の累積（期間前から）と、期間内の回答者のうち要注意がある人の割合 */
export const lifestyleSummary = (report: ICompanyReport) => {
  const inPeriod = report.series.reduce(
    (acc, w) => acc + point(w, 'lifestyleFailed').value,
    0,
  );

  return {
    cumulativeFailed: report.lifestyle.failedBefore + inPeriod,
    failedInPeriod: inPeriod,
    respondents: report.lifestyle.respondents,
    attentionRespondents: report.lifestyle.attentionRespondents,
    attentionPct: percent(
      report.lifestyle.attentionRespondents,
      report.lifestyle.respondents,
    ),
  };
};

const NOUKNOW_RANKS = ['A', 'B', 'C', 'none'] as const;

/** のうKNOW: A / B / C / 判定なし の人数と割合（並びは固定） */
export const nouknowSummary = (report: ICompanyReport) => {
  const total = report.nouknow.reduce((acc, r) => acc + r.users, 0);

  return {
    total,
    ranks: NOUKNOW_RANKS.map((rank) => {
      const users = report.nouknow.find((r) => r.rank === rank)?.users ?? 0;

      return {
        rank,
        label: rank === 'none' ? '判定なし' : `${rank}判定`,
        users,
        pct: percent(users, total),
      };
    }).filter((r) => r.rank !== 'none' || r.users > 0),
  };
};

const HENKA_METRICS: {
  metric: 'steps' | 'driving' | 'brain' | 'activity';
  label: string;
}[] = [
  { metric: 'steps', label: '歩数' },
  { metric: 'driving', label: '運転' },
  { metric: 'brain', label: '脳トレ' },
  { metric: 'activity', label: '家族の活動量' },
];

/** 変化検知: 指標ごとの良い変化 / 注意の件数と、週あたりの件数 */
export const henkaSummary = (report: ICompanyReport) =>
  HENKA_METRICS.map(({ metric, label }) => {
    const of = (direction: 'good' | 'bad') =>
      report.henka.decisions
        .filter((d) => d.metric === metric && d.direction === direction)
        .reduce((acc, d) => acc + d.detections, 0);
    const good = of('good');
    const bad = of('bad');

    return {
      metric,
      label,
      good,
      bad,
      perWeek:
        report.henka.runWeeks > 0 ? (good + bad) / report.henka.runWeeks : null,
    };
  });

/** 限定記事: 記事ごとの開封率（開いた人 ÷ 対象家族のメンバー）と、カテゴリ内の平均 */
export const articleSummary = (report: ICompanyReport) =>
  report.articles.map((group) => {
    const articles = group.articles.map((a) => ({
      ...a,
      openPct: percent(a.openedUsers, report.members),
    }));
    const rates = articles
      .map((a) => a.openPct)
      .filter((v): v is number => v !== null);

    return {
      categoryId: group.categoryId,
      categoryName: group.categoryName ?? 'カテゴリなし',
      averagePct: rates.length
        ? Math.round(
            (rates.reduce((acc, v) => acc + v, 0) / rates.length) * 10,
          ) / 10
        : null,
      articles,
    };
  });
